import React, { Component } from 'react'
import { connect } from 'react-redux'
import compose from 'lodash/fp/compose'
import isEqual from 'lodash/isEqual'
import config from 'config'
import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'
import {
  loadKidsSections,
  loadRootShowcaseNavigation,
  loadShowcaseSections,
} from 'client/bookmate/reducers/showcase-reducer'
import { loadMetaData } from 'client/shared/reducers/meta-reducer'

import {
  getShowcaseAttributes,
  getShowcaseSections,
  getShowcaseTitle,
  getShowcaseUuid,
  getShowcaseBackground,
  getShowcaseHierarchy,
  getRootNavigation,
  getShowcaseFrom,
  getShowcaseLimit,
} from 'client/bookmate/selectors/showcase-selectors'
import { getMetaData } from 'client/shared/selectors/meta-selectors'

import {
  getResourceFromPathname,
  getContentTypeByResource,
} from 'client/bookmate/helpers/showcase-helpers'

import Meta from 'client/shared/blocks/meta'
import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import MetaDeeplink from 'client/shared/blocks/meta-deeplink'

import HeaderBox from 'client/shared/boxes/header-box'
import Spacer from 'client/shared/blocks/spacer'
import Footer from 'client/shared/blocks/footer'

import { State as AppState } from 'client/shared/reducers/app-reducer'
import { Dispatch, State } from 'shared/types/redux'
import { HierarchyProps } from 'client/shared/types/topic'
import { Params, Location } from 'client/shared/types/react-router'
import { MetaData } from 'client/shared/types/resource'
import {
  ShowcaseAttributes,
  ShowcaseSection,
  RootNavigation,
} from 'client/shared/types/showcase'
import { Locale } from 'client/shared/types/current-user'
import { QueryParams } from 'shared/tools/url-helper'
import KidsContentBox from '../boxes/kids-content-box'

type Props = {
  app: AppState
  params: Params
  dispatch: Dispatch
  location: Location
  attributes: ShowcaseAttributes
  sections: {
    items: ShowcaseSection[]
    loading: boolean
  }
  companySubscription?: string
  rootNavigation: RootNavigation
  uuid: string
  title?: string
  background?: {
    large: string
  }
  hierarchy: HierarchyProps[]
  libraryLang: string
  loadSectionsFrom?: number
  limit?: number
  meta: MetaData
}

async function loadShowcase({
  dispatch,
  rootNavigation,
  currentSections,
}: {
  dispatch: Dispatch
  resource: string
  rootNavigation: RootNavigation
  searchQuery?: string
  currentSections: ShowcaseSection[]
  libraryLang: Locale
  query?: QueryParams
}) {
  if (!rootNavigation?.items?.length) return
  const kids = rootNavigation.items[1]
  const currentSlug = kids?.params?.slug

  if (!currentSections.length) {
    await dispatch(
      loadKidsSections({
        currentSlug,
        withHierarchy: false,
        language: 'sr',
        contentType: 'mixed',
      }),
    )
  }
}

class KidsShowcase extends Component<Props> {
  componentDidUpdate(prevProps: Props): void {
    const {
      dispatch,
      rootNavigation,
      location,
      sections,
      uuid,
      libraryLang,
    } = this.props

    const resource = getResourceFromPathname(location.pathname)
    const prevResource = getResourceFromPathname(prevProps.location.pathname)
    const isRootNavigationUpdated = !isEqual(
      prevProps.rootNavigation.items,
      rootNavigation.items,
    )

    if (isRootNavigationUpdated || prevProps.libraryLang !== libraryLang) {
      dispatch(loadRootShowcaseNavigation(libraryLang))
    }

    if (isRootNavigationUpdated || prevResource !== resource) {
      loadShowcase({
        dispatch,
        resource,
        rootNavigation,
        currentSections: sections.items,
        libraryLang: libraryLang as Locale,
        query: this.props.app.storedQuery,
      })
    }

    if (uuid && uuid !== prevProps.uuid) {
      loadMetaData({
        resourceName: 'showcases',
        resourceUuid: uuid,
        language: libraryLang,
        contentType: getContentTypeByResource(resource),
      })
    }
  }

  load = (): void => {
    const { dispatch, loadSectionsFrom, limit } = this.props
    const currentSlug = DECE

    return dispatch(
      loadShowcaseSections({
        currentSlug,
        withHierarchy: true,
        data: { from: loadSectionsFrom, limit },
      }),
    )
  }

  render(): JSX.Element {
    const { params } = this.props
    const currentSlug = DECE

    return (
      <>
        {this.renderMeta()}
        <HeaderBox />
        <KidsContentBox
          currentSlug={currentSlug}
          load={this.load}
          sectionSlug={params.sectionSlug}
        />
        <Spacer size="bottom-page" />
        <Footer />
      </>
    )
  }

  renderMeta(): JSX.Element {
    const { title: resourceTitle, meta } = this.props

    const title = (meta && meta.title) || resourceTitle
    const description = meta && meta.description

    return (
      <>
        {title && (
          <MetaTitle
            title={title}
            disableSuffix={meta && Boolean(meta.title)}
          />
        )}
        {description && <MetaDescription description={description} />}
        <Meta
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          content={`${ASSETS_PATH}sharing/main.png?v4`}
          property="twitter:image"
        />
        <Meta
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          content={`${ASSETS_PATH}sharing/main.png?v4`}
          property="og:image"
        />
        <MetaDeeplink target="showcase" />
      </>
    )
  }
}

export const DECE = 'decije-knjige-i-lektire'

const prepareWrapper = prepareComponent(async function ({ store, location }) {
  const { dispatch, getState } = store
  const state = getState()
  const resource = getResourceFromPathname(location.pathname)
  const contentType = getContentTypeByResource(resource)
  const libraryLang =
    state.currentUser.data.library_lang || config.fallbackLocale

  if (!state.showcase.rootNavigation.items.length)
    await dispatch(loadRootShowcaseNavigation(libraryLang))

  const currentSlug = DECE

  const rootNavigation = getRootNavigation(getState())
  const sections = getShowcaseSections(getState(), currentSlug)
  await loadShowcase({
    dispatch,
    resource,
    rootNavigation,
    searchQuery: location.search,
    currentSections: sections.items,
    libraryLang,
  })

  const uuid = getShowcaseUuid(getState(), currentSlug)
  if (!uuid) return null

  await dispatch(
    loadMetaData({
      resourceName: 'showcases',
      resourceUuid: uuid,
      language: libraryLang,
      contentType,
    }),
  )
})

const connectWrapper = connect(
  (state: State, ownProps: { location: Location }) => {
    const { location } = ownProps
    const resource = getResourceFromPathname(location.pathname)
    const contentType = getContentTypeByResource(resource)
    const libraryLang = state.currentUser.data.library_lang
    const currentSlug = DECE
    const uuid = getShowcaseUuid(state, currentSlug)

    return {
      app: state.app,
      user: state.currentUser,
      rootNavigation: getRootNavigation(state),
      libraryLang,
      attributes: getShowcaseAttributes(state, currentSlug),
      sections: getShowcaseSections(state, currentSlug),
      title: getShowcaseTitle(state, currentSlug),
      uuid,
      background: getShowcaseBackground(state, currentSlug),
      hierarchy: getShowcaseHierarchy(state, currentSlug),
      loadSectionsFrom: getShowcaseFrom(state, currentSlug),
      limit: getShowcaseLimit(state, currentSlug),
      companySubscription: state.currentUser.data.company_subscription,
      meta: uuid
        ? getMetaData(state, {
            resourceName: 'showcases',
            resourceUuid: uuid,
            language: libraryLang,
            contentType,
          })
        : null,
    }
  },
)

const wrappers = compose(prepareWrapper, errorBoundary, connectWrapper)

export default wrappers(KidsShowcase)
